/**
 * PMS (Pantone) shade palette + lookup helpers for the store-editor
 * Brand Color picker.
 *
 * WHY A PMS CODE AND NOT JUST A HEX
 * ---------------------------------
 * A distributor's brand color is a SPOT color -- the thing a production
 * shop matches ink or thread to. A screen hex cannot represent it
 * faithfully (spot inks live outside sRGB, and the rendered value shifts
 * with the display). So the CODE is authoritative for production and the
 * hex is only ever an on-screen approximation. Every surface that shows a
 * swatch must say so; see StoreBranding.vue's approximation note.
 *
 * WHERE THE DATA COMES FROM
 * -------------------------
 * `pmsPalette.json` is GENERATED by `tools/gen-pms-palette.py` from two
 * Madeira thread-matching charts already vendored in this repo for the
 * Wilcom integration (docs/integrations/pantone/). No new third-party
 * dependency, no licensed Pantone library, and the .xlsx files are never
 * read at runtime. That script documents the two de-duplication rules
 * (Polyneon wins across charts; first row wins within a chart).
 *
 * COVERAGE IS PARTIAL AND THAT IS BY DESIGN
 * -----------------------------------------
 * 576 codes, drawn from embroidery thread charts -- not the ~2100-shade
 * PANTONE Coated set. A brand color outside this list is ordinary, not an
 * error. `lookupHex` returning null is the signal the editor uses to ask
 * the distributor for the hex themselves rather than guessing one.
 */

import paletteDocument from "./pmsPalette.json";

/**
 * The full palette: `[{ code, hex, chart, thread }]`, sorted numerically
 * then by name. Frozen because it is module-global shared state -- a
 * component that sorted or spliced it in place would corrupt the picker
 * for every other consumer.
 */
export const PMS_SHADES = Object.freeze(
    (paletteDocument.shades || []).map((shade) => Object.freeze({ ...shade }))
);

/** O(1) lookup keyed by the canonical uppercase code. */
const SHADES_BY_CODE = new Map(
    PMS_SHADES.map((shade) => [shade.code.toUpperCase(), shade])
);

/**
 * Bound on a persisted PMS code. The DB column is String(32); keeping the
 * client bound identical means the inline error fires before a value the
 * server would reject is ever sent.
 */
export const PMS_CODE_MAX_LENGTH = 32;

/**
 * Allowed shape for a PMS code. Real codes are alphanumeric words with
 * single spaces ("286 C", "Black 6 C", "Cool Gray 11 C", "10107 C"), so
 * the allowlist is letters/digits/space plus `-` and `/`, which appear in
 * some Pantone naming. Everything else -- angle brackets, quotes,
 * semicolons, newlines -- is rejected rather than escaped, because this
 * value is free text from a user that lands in the database and is later
 * rendered back to other users.
 */
const PMS_CODE_ALLOWED_RE = /^[A-Za-z0-9][A-Za-z0-9 /-]*$/;

/**
 * Canonicalize a user-typed or vendor-supplied code to our `PMS <...>`
 * form: single-spaced, uppercased, with exactly one `PMS ` prefix.
 *
 * Accepts what users actually type -- "286 c", "pantone 286 C", "PMS 286
 * C" -- and returns "PMS 286 C" for all three so equality checks and the
 * palette lookup behave. A bare number gains the prefix ("286" ->
 * "PMS 286"); we do NOT invent a finish suffix, since we cannot know
 * whether the user means Coated or Uncoated.
 *
 * @param {*} value raw user input.
 * @returns {string} canonical code, or "" when the input is blank.
 */
export function normalizePmsCode(value) {
    if (typeof value !== "string") return "";
    const collapsed = value.trim().replace(/\s+/g, " ");
    if (!collapsed) return "";
    const withoutPrefix = collapsed.replace(/^(PMS|PANTONE)\s+/i, "");
    if (!withoutPrefix) return "";
    return `PMS ${withoutPrefix.toUpperCase()}`;
}

/**
 * Strict validator for a free-text PMS code that is about to be persisted.
 *
 * This is a security boundary, not just a UX nicety: the value is user
 * input bound for the database and for later render. It is length-bounded
 * to the column width and restricted to a conservative character
 * allowlist. The server re-validates independently
 * (`api/src/models/store.py::_validate_brand_pms_code`) -- this check
 * exists to give an inline error, never as the only gate.
 *
 * @param {*} value raw user input.
 * @returns {boolean} true iff the value is safe and plausibly a PMS code.
 */
export function isValidPmsCode(value) {
    if (typeof value !== "string") return false;

    // Check the RAW input for control characters (newline, tab, NUL, ...)
    // BEFORE normalization. normalizePmsCode collapses any \s+ run into a
    // single space, which would otherwise launder an embedded newline into
    // a code that looks perfectly valid -- exactly the kind of value we do
    // not want reaching the database or a log line.
    // eslint-disable-next-line no-control-regex
    if (/[\u0000-\u001F\u007F]/.test(value)) return false;

    const collapsed = value.trim().replace(/ +/g, " ");
    if (!collapsed) return false;

    const canonical = normalizePmsCode(collapsed);
    if (canonical.length > PMS_CODE_MAX_LENGTH) return false;

    // Validate the part after the canonical prefix so the prefix's own
    // space does not have to be encoded in the allowlist.
    const body = canonical.slice("PMS ".length);
    return PMS_CODE_ALLOWED_RE.test(body);
}

/**
 * Look up the on-screen approximation hex for a PMS code.
 *
 * @param {*} code a code in any of the accepted input forms.
 * @returns {string|null} `#RRGGBB` when the code is in the vendored
 *   charts, otherwise `null` -- which the store editor treats as "ask the
 *   distributor for the hex" rather than as an error.
 */
export function lookupHex(code) {
    const canonical = normalizePmsCode(code);
    if (!canonical) return null;
    const shade = SHADES_BY_CODE.get(canonical.toUpperCase());
    return shade ? shade.hex : null;
}

/**
 * Full shade record for a code, or null. Same lookup contract as
 * `lookupHex`, for callers that also want the chart/thread provenance.
 */
export function findShade(code) {
    const canonical = normalizePmsCode(code);
    if (!canonical) return null;
    return SHADES_BY_CODE.get(canonical.toUpperCase()) || null;
}

/**
 * Search the palette for the picker's type-ahead.
 *
 * Matches a substring of the canonical code, so a user typing the bare
 * number ("1935") or a name fragment ("black 6") or the full thing
 * ("PMS 1935 C") all hit. Case-insensitive; whitespace-collapsed so
 * "black  6" behaves like "black 6".
 *
 * @param {*} query raw text from the search field.
 * @returns {Array} matching shades, in palette order. An empty query
 *   returns the whole palette (the picker opens showing everything).
 */
export function searchShades(query) {
    if (typeof query !== "string") return PMS_SHADES.slice();
    const needle = query.trim().replace(/\s+/g, " ").toUpperCase();
    if (!needle) return PMS_SHADES.slice();

    // Let the user type the prefix without it narrowing anything, since
    // every code carries it.
    const bare = needle.replace(/^(PMS|PANTONE)\s+/, "");
    if (!bare) return PMS_SHADES.slice();

    // Substring matching alone is wrong at the boundary: a user searching
    // their exact code "286 C" would be shown "9286 C" (and, worse, might
    // pick it). So match on WORD boundaries -- the query must align to the
    // start of a word within the code -- and rank whole-code matches
    // first. "286" still finds "286 C"; it no longer finds "9286 C".
    const matches = PMS_SHADES.map((shade) => {
        const body = shade.code.slice("PMS ".length).toUpperCase();
        if (body === bare) return { shade, rank: 0 };
        if (body.startsWith(bare)) return { shade, rank: 1 };
        // Interior match, but only at a word start ("BLACK 6" in
        // "BLACK 6 C", "GRAY 11" in "COOL GRAY 11 C").
        if (body.includes(` ${bare}`)) return { shade, rank: 2 };
        return null;
    }).filter(Boolean);

    matches.sort((a, b) => a.rank - b.rank);
    return matches.map((m) => m.shade);
}
